import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { LoggerUtils, DateUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { EnvironmentUtils } from 'utils'
import { getConfiguration, getNotificationsNumbers } from 'core/environment/api/environmentService'
import { userLoggedOut, clearSessionInfo, logoutUser } from 'core/session/slices/session'
import { redirectToSta } from 'core/staIntegration/slices/staIntegration'
import {
  EnvironmentState,
  ApiConfigurationResponse,
  ApiNotificationsCountersResponse,
} from './types'

const initialState: EnvironmentState = {
  configuration: {},
  notificationsCounters: {},
}

const environmentSlice = createSlice({
  name: 'environment',
  initialState,
  reducers: {
    clientConfiguration(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiConfigurationResponse>>
    ) {
      state.configuration = action.payload
    },
    notificationsCountersLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiNotificationsCountersResponse>>
    ) {
      state.notificationsCounters = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { clientConfiguration, notificationsCountersLoaded } = environmentSlice.actions

export default environmentSlice.reducer

const handleClientConfiguration =
  (response: ApiMiddlewarePayload<ApiConfigurationResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response

    if (error) {
      dispatch(logoutUser())
      return
    }
    if (result?.configuration) {
      const { configuration } = result
      const { isOceanLearningPlatformEnabled } = configuration.permissions.general
      if (!isOceanLearningPlatformEnabled) {
        LoggerUtils.info('Ocean Learning Platform Disabled')
        dispatch(redirectToSta())
        dispatch(clearSessionInfo())
        return
      }
      EnvironmentUtils.storeEnvironmentConfiguration(configuration)
      DateUtils.setCustomerDateTimeFormat(configuration.customerDateTimeFormat)
    }

    dispatch(clientConfiguration(response))
  }

export const loadClientConfiguration = () => {
  return createApiAction({
    apiCalls: {
      configuration: getConfiguration,
    },
    payloadAction: clientConfiguration,
    nextAction: handleClientConfiguration,
    shouldCallApi: () => !EnvironmentUtils.getEnvironmentConfiguration(),
    enableErrorNotification: true,
  })
}

export const loadNotificationsCounters = () => {
  return createApiAction({
    apiCalls: {
      counters: getNotificationsNumbers,
    },
    payloadAction: notificationsCountersLoaded,
    enableErrorNotification: true,
  })
}
